/**
 * MobileCreate — immersive mobile create experience (iter-74).
 *
 * Per the design spec, this is no longer a form / generator / utility tool.
 * It's a "private emotional studio" with:
 *   • Sticky MobileTopBar (hamburger | green-square + wordmark | bell)
 *   • Big breathing headline ("what are you feeling right now?")
 *   • Large textarea with rotating ambient placeholders
 *   • Horizontal tone rail (Auto · Reflective · Bold · Soft · Faith · Romantic · Dark)
 *   • Confident primary CTA "Make my MoodCard" with soft glow
 *   • Subtle ambient gradient atmosphere
 *
 * Mounted only on mobile; desktop continues to render the legacy Home layout.
 * Receives all its state + handlers via props from the parent Home so the
 * data flow (navigate to /loading, personalize, useAI) stays intact.
 */
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { tap } from "../../lib/haptic";
import { track } from "../../lib/track";
import LiveTakeoverBanner from "../LiveTakeoverBanner";
import ImpactBanner from "../ImpactBanner";
import MarqueeRail from "../MarqueeRail";

const ROTATING_PLACEHOLDERS = [
    "i wish they knew.",
    "i'm tired of pretending.",
    "i miss who i used to be.",
    "i almost sent this.",
    "some feelings don't fit in messages.",
    "i'm fine — but i'm not.",
    "i think about you more than i'd admit.",
    "today felt heavy for no reason.",
];

const TONES = [
    { key: "auto",       label: "Auto"       },
    { key: "reflective", label: "Reflective" },
    { key: "bold",       label: "Bold"       },
    { key: "tender",     label: "Soft"       },  // backend uses 'tender' for soft
    { key: "faith",      label: "Faith"      },
    { key: "romantic",   label: "Romantic"   },
    { key: "dark",       label: "Dark"       },
];

export default function MobileCreate({
    feeling,
    setFeeling,
    activeTone,
    onToneChange,
    spice,
    onSpiceChange,
    quota,
    activeChallengeSlug,
    onClearChallenge,
    onSubmit,
}) {
    const [phIdx, setPhIdx] = useState(0);
    const textareaRef = useRef(null);
    const toneRailRef = useRef(null);

    // iter-100 — persist the LAST direction + spice picks so a returning user
    // doesn't have to re-select their feel every session. `auto` (null) stays
    // the default for first-timers. Writes go through onToneChange/
    // onSpiceChange in the parent; we only restore on mount.
    useEffect(() => {
        try {
            const savedTone = window.localStorage.getItem("moodcard.last_tone");
            const savedSpice = window.localStorage.getItem("moodcard.last_spice");
            if (savedTone && savedTone !== "auto" && !activeTone && onToneChange) {
                onToneChange(savedTone);
            }
            if (savedSpice && (!spice || spice === "bold") && onSpiceChange) {
                onSpiceChange(savedSpice);
            }
        } catch { /* ignore localStorage failures */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        try {
            if (activeTone) window.localStorage.setItem("moodcard.last_tone", activeTone);
            else window.localStorage.removeItem("moodcard.last_tone");
        } catch { /* ignore */ }
    }, [activeTone]);
    useEffect(() => {
        try {
            if (spice) window.localStorage.setItem("moodcard.last_spice", spice);
        } catch { /* ignore */ }
    }, [spice]);

    // Rotate placeholder every 4.5s while idle (per spec — ambient breathing).
    // Hard-pause when the user has started typing so we never destroy their context.
    useEffect(() => {
        if ((feeling || "").length > 0) return;
        const t = setInterval(() => setPhIdx((i) => (i + 1) % ROTATING_PLACEHOLDERS.length), 4500);
        return () => clearInterval(t);
    }, [feeling]);

    // When user picks a tone, smooth-scroll the rail so the active chip stays
    // centered in view — matches the "auto scroll horizontal" request.
    useEffect(() => {
        if (!toneRailRef.current) return;
        const active = toneRailRef.current.querySelector('[data-active="true"]');
        if (!active) return;
        active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [activeTone]);

    const canSubmit = (feeling || "").trim().length > 0;

    return (
        <div className="lg:hidden relative min-h-[calc(100vh-3rem)] px-5 pb-8" data-testid="mobile-create">
            {/* Ambient atmospheric gradient — breathes softly. Pointer-events:none
                so it never blocks input taps. */}
            <motion.div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                animate={{
                    background: [
                        "radial-gradient(ellipse at 18% 22%, rgba(167,139,250,0.16), transparent 55%), radial-gradient(ellipse at 78% 70%, rgba(56,189,248,0.10), transparent 60%)",
                        "radial-gradient(ellipse at 78% 28%, rgba(252,211,77,0.10), transparent 55%), radial-gradient(ellipse at 22% 75%, rgba(167,139,250,0.14), transparent 60%)",
                        "radial-gradient(ellipse at 18% 22%, rgba(167,139,250,0.16), transparent 55%), radial-gradient(ellipse at 78% 70%, rgba(56,189,248,0.10), transparent 60%)",
                    ],
                }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Hero headline — emotionally inviting, cinematic typography */}
            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="pt-10 pb-7"
                data-testid="mobile-create-hero"
            >
                <div className="text-[10px] tracking-[0.32em] uppercase text-amber-300/70 mb-3">
                    private studio
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-white leading-[1.1] tracking-tight">
                    what are you<br />
                    <span className="italic font-serif text-white/90">feeling</span>
                    <span className="text-white/80"> right now?</span>
                </h1>
                <p className="mt-3 font-serif italic text-white/45 text-sm leading-snug max-w-[22rem]">
                    say it once. we'll turn it into something you can actually share.
                </p>
            </motion.section>

            {/* iter-95 — live game takeover banner + impact digest. Both
                self-hide when there's nothing to surface. */}
            <div className="pt-2 space-y-2">
                <LiveTakeoverBanner />
                <ImpactBanner />
            </div>

            {/* Challenge pill (if joined) */}
            {activeChallengeSlug && (
                <div className="mb-3 flex items-center justify-end">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-300/25 text-amber-200/90 text-[10px] tracking-[0.18em] uppercase font-medium" data-testid="active-challenge-pill">
                        <Sparkles className="w-3 h-3" />
                        <span>joined</span>
                        <span className="font-serif italic normal-case tracking-normal text-amber-100/95">{activeChallengeSlug}</span>
                        <button
                            type="button"
                            onClick={onClearChallenge}
                            className="ml-1 text-amber-300/55 hover:text-amber-100 transition-colors"
                            aria-label="leave challenge"
                        >
                            ×
                        </button>
                    </span>
                </div>
            )}

            {/* Textarea — large, glowing on focus, rotating placeholder */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
                data-testid="mobile-create-input-wrap"
            >
                <textarea
                    ref={textareaRef}
                    value={feeling}
                    onChange={(e) => setFeeling(e.target.value)}
                    onFocus={() => {
                        // iter-95/100 — keyboard-aware scroll. On focus, give the
                        // mobile keyboard ~280ms to render, then scroll the
                        // input into the upper-middle of the visible viewport.
                        // Also listen to visualViewport.resize for ~2s so
                        // late-rendering keyboards (Android) still snap the
                        // input into view.
                        const lift = () => {
                            try {
                                const node = textareaRef.current;
                                if (!node) return;
                                const vv = window.visualViewport;
                                const vvTop = vv?.offsetTop || 0;
                                const vvHeight = vv?.height || window.innerHeight;
                                const rect = node.getBoundingClientRect();
                                const target = vvTop + Math.max(56, vvHeight * 0.16);
                                const delta = rect.top - target;
                                if (Math.abs(delta) > 4) {
                                    window.scrollBy({ top: delta, behavior: "smooth" });
                                }
                            } catch { /* ignore */ }
                        };
                        setTimeout(lift, 280);
                        // Re-lift on viewport resize (keyboard show/hide changes vvHeight).
                        try {
                            const vv = window.visualViewport;
                            if (vv) {
                                const handler = () => { lift(); };
                                vv.addEventListener("resize", handler);
                                setTimeout(() => vv.removeEventListener("resize", handler), 2000);
                            }
                        } catch { /* ignore */ }
                    }}
                    rows={5}
                    autoFocus={false}
                    spellCheck="true"
                    aria-label="what are you feeling"
                    placeholder={ROTATING_PLACEHOLDERS[phIdx]}
                    data-testid="mobile-create-textarea"
                    style={{
                        backgroundColor: "rgba(255,255,255,0.04)",
                        borderColor: "rgba(255,255,255,0.10)",
                    }}
                    className="w-full resize-none rounded-2xl border focus:border-amber-300/55 focus:bg-white/[0.06] transition-all px-4 py-4 font-serif italic text-white text-lg sm:text-xl leading-relaxed placeholder-white/35 focus:outline-none focus:shadow-[0_0_36px_-6px_rgba(252,211,77,0.35)]"
                />
                {/* Live char hint — fades up after 12 chars; emotional reassurance */}
                <AnimatePresence>
                    {(feeling || "").length >= 12 && (
                        <motion.span
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            className="absolute right-3 bottom-3 text-[10px] tracking-[0.18em] uppercase text-white/35 font-sans pointer-events-none"
                        >
                            keep going
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Direction rail — tones (auto-selected by default) */}
            <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.16, ease: [0.16, 1, 0.3, 1] }}
                className="mt-5"
                data-testid="mobile-create-tone-rail"
            >
                <div className="text-[11px] tracking-[0.32em] uppercase text-white/55 mb-2 px-1">
                    direction
                </div>
                <MarqueeRail
                    className="-mx-5 px-5 pb-2"
                    testId="mobile-create-tone-marquee"
                    speed={0.22}
                >
                    {TONES.map((t) => {
                        const isActive =
                            (t.key === "auto" && !activeTone) ||
                            activeTone === t.key;
                        return (
                            <motion.button
                                key={t.key}
                                type="button"
                                whileTap={{ scale: 0.95 }}
                                data-active={isActive ? "true" : "false"}
                                onClick={() => {
                                    tap();
                                    track("create_tone_pick", { tone: t.key });
                                    onToneChange(t.key === "auto" ? null : t.key);
                                }}
                                className={`shrink-0 px-5 py-3 rounded-full border text-[17px] font-serif italic whitespace-nowrap transition-all ${
                                    isActive
                                        ? "bg-amber-400/30 border-amber-300 text-amber-50 shadow-[0_0_28px_-2px_rgba(252,211,77,0.85),inset_0_0_0_1px_rgba(252,211,77,0.4)] scale-[1.04]"
                                        : "bg-white/[0.04] border-white/12 text-white/60 hover:bg-white/[0.08]"
                                }`}
                                data-testid={`mobile-create-tone-${t.key}`}
                            >
                                {t.label}
                            </motion.button>
                        );
                    })}
                </MarqueeRail>
            </motion.section>

            {/* Quota chip — gentle bottom-out warning */}
            {quota && quota.used > 0 && quota.remaining <= 2 && (
                <div className="mt-4 flex items-center justify-end gap-2" data-testid="mobile-create-quota">
                    <span className="text-[10px] tracking-[0.18em] uppercase text-amber-200/85">
                        {quota.remaining === 0 ? "none left today" : `${quota.remaining} feelings left today`}
                    </span>
                </div>
            )}

            {/* iter-76: Spice dial — controls how daring the AI gets.
                Defaults to "bold" (new house style). Sits BELOW the direction rail
                because it adjusts the same axis at a different granularity. */}
            <section className="mt-5" data-testid="mobile-create-spice-rail">
                <div className="text-[11px] tracking-[0.32em] uppercase text-white/55 mb-2 px-1 flex items-center justify-between">
                    <span>spice</span>
                    <span className="text-white/30 normal-case tracking-normal italic font-serif text-xs">
                        how daring should the AI get?
                    </span>
                </div>
                <MarqueeRail
                    className="-mx-5 px-5 pb-1"
                    testId="mobile-create-spice-marquee"
                    speed={0.18}
                >
                    {[
                        { key: "mild",     label: "Mild",      sub: "gentle" },
                        { key: "bold",     label: "Bold",      sub: "default" },
                        { key: "chaotic",  label: "Chaotic",   sub: "internet-coded" },
                        { key: "unhinged", label: "Unhinged",  sub: "full delulu" },
                    ].map((s) => {
                        const active = (spice || "bold") === s.key;
                        return (
                            <motion.button
                                key={s.key}
                                type="button"
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                    tap();
                                    track("create_spice_pick", { spice: s.key });
                                    onSpiceChange?.(s.key);
                                }}
                                className={`shrink-0 px-4 py-2.5 rounded-2xl border whitespace-nowrap text-left transition-all ${
                                    active
                                        ? "bg-rose-400/25 border-rose-300 text-rose-50 shadow-[0_0_28px_-2px_rgba(244,114,182,0.85),inset_0_0_0_1px_rgba(244,114,182,0.4)] scale-[1.04]"
                                        : "bg-white/[0.04] border-white/10 text-white/60 hover:bg-white/[0.08]"
                                }`}
                                data-testid={`mobile-create-spice-${s.key}`}
                            >
                                <div className="text-[15px] font-medium">{s.label}</div>
                                <div className="text-[10px] tracking-[0.18em] uppercase opacity-65 mt-0.5">
                                    {s.sub}
                                </div>
                            </motion.button>
                        );
                    })}
                </MarqueeRail>
            </section>

            {/* Primary CTA — large, confident, soft glow */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
                className="mt-7"
            >
                <motion.button
                    type="button"
                    disabled={!canSubmit}
                    whileTap={canSubmit ? { scale: 0.97 } : undefined}
                    onClick={() => { tap(); onSubmit(); }}
                    className={`relative w-full py-4 rounded-full font-medium text-base tracking-wide transition-all overflow-hidden ${
                        canSubmit
                            ? "bg-amber-400 text-black shadow-[0_0_36px_-6px_rgba(252,211,77,0.55)] hover:bg-amber-300"
                            : "bg-white/5 text-white/35 cursor-not-allowed"
                    }`}
                    data-testid="mobile-create-submit"
                >
                    {/* Subtle gradient sweep on the active CTA — adds the "alive" feeling */}
                    {canSubmit && (
                        <motion.span
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            animate={{
                                background: [
                                    "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
                                    "linear-gradient(120deg, transparent 70%, rgba(255,255,255,0.35) 90%, transparent 110%)",
                                ],
                            }}
                            transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
                        />
                    )}
                    <span className="relative inline-flex items-center justify-center gap-2">
                        Make my MoodCard
                        <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                    </span>
                </motion.button>
            </motion.div>

            {/* Tiny tap hint, fades after first scroll/tap */}
            <p className="mt-5 text-center text-[10px] tracking-[0.22em] uppercase text-white/30">
                no sign-up · 5 free a day
            </p>
        </div>
    );
}
